import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin, Users, ExternalLink, Copy, Trash2, Clock, Mail, Phone, Pencil, DollarSign, CheckCircle2, XCircle } from "lucide-react";
import { formatLongDate, formatTime, formatShortDate } from "@/lib/dateUtils";
import { EVENT_TYPE_LABELS, formatMoney } from "@/lib/eventTypes";

export default function EventManage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [signups, setSignups] = useState([]);
  const [calSlots, setCalSlots] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data: events } = await api.get("/events");
      const found = events.find((e) => e.id === id);
      if (!found) throw new Error("No encontrado");
      setEvent(found);

      if (found.event_type === "calendar") {
        const { data } = await api.get(`/events/${id}/admin/calendar`);
        setCalSlots(data.slots || []);
      } else if (found.event_type === "potluck" || !found.event_type) {
        const { data: su } = await api.get(`/events/${id}/signups`);
        setSignups(su);
      } else {
        const { data: subs } = await api.get(`/events/${id}/submissions`);
        setSubmissions(subs);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [id]);

  const publicUrl = event ? `${window.location.origin}/e/${event.slug}` : "";
  const isCalendar = event?.event_type === "calendar";
  const isPotluck = event?.event_type === "potluck" || !event?.event_type;
  const settings = event?.calendar_settings || {};
  const timeFormat = settings.time_format || "12h";

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Link copiado");
  };

  const removeSignup = async (sid) => {
    if (!window.confirm("¿Quitar este apuntado?")) return;
    try {
      await api.delete(`/events/${id}/signups/${sid}`);
      toast.success("Registro eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeBooking = async (slotId) => {
    if (!window.confirm("¿Cancelar esta reserva?")) return;
    try {
      await api.delete(`/events/${id}/calendar-slots/${slotId}/booking`);
      toast.success("Reserva cancelada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const slotsByDate = useMemo(() => {
    const map = new Map();
    for (const s of calSlots) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [calSlots]);

  const totalBookings = calSlots.filter((s) => s.booked).length;

  if (loading) return <div className="min-h-screen bg-cream-50"><Navbar /><p className="p-10 text-ink-500">Cargando…</p></div>;
  if (!event) return null;

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <span className="text-xs uppercase tracking-[0.24em] text-coral">Administración</span>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-6">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl text-ink-900 font-light leading-tight">
              {event.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-ink-500 text-sm">
              {event.event_date && <span className="flex items-center gap-1.5"><Calendar size={14} strokeWidth={1.75} />{event.event_date}</span>}
              {event.location && <span className="flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.75} />{event.location}</span>}
              <span className="flex items-center gap-1.5">
                <Users size={14} strokeWidth={1.75} />
                {isCalendar ? `${totalBookings} / ${calSlots.length} slots reservados` : isPotluck ? `${signups.length} registros` : `${submissions.length} registros`}
              </span>
              {(event.total_collected || 0) > 0 && (
                <span className="flex items-center gap-1.5 text-sage">
                  <DollarSign size={14} strokeWidth={1.75} />
                  {formatMoney(event.total_collected, event.goal?.currency || event.dues_config?.currency || "MXN")} recaudado
                </span>
              )}
              {!isPotluck && (
                <span className="text-[10px] uppercase tracking-widest bg-coral/10 text-coral rounded-full px-2 py-0.5">
                  {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/eventos/${id}/editar`}>
              <Button variant="outline" className="rounded-full border-line hover:bg-cream-100" data-testid="edit-event-btn">
                <Pencil size={16} strokeWidth={1.75} className="mr-2" /> Editar
              </Button>
            </Link>
            <Link to={`/e/${event.slug}`} target="_blank">
              <Button variant="outline" className="rounded-full border-line hover:bg-cream-100" data-testid="open-public-btn">
                <ExternalLink size={16} strokeWidth={1.75} className="mr-2" /> Ver página pública
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-8 bg-white border border-line rounded-2xl p-5 flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-[0.2em] text-ink-500">Link para compartir</span>
          <code className="text-sm text-ink-700 bg-cream-100 px-3 py-1.5 rounded-lg truncate max-w-full" data-testid="public-link">
            {publicUrl}
          </code>
          <Button size="sm" onClick={copyLink} className="rounded-full bg-ink-900 hover:bg-ink-700 text-white" data-testid="copy-link-btn">
            <Copy size={14} strokeWidth={1.75} className="mr-1.5" /> Copiar
          </Button>
        </div>

        {isCalendar ? (
          <div className="mt-10 space-y-6">
            <h2 className="font-heading text-2xl text-ink-900">Reservas por día</h2>
            {Array.from(slotsByDate.entries()).map(([date, list]) => (
              <div key={date} className="rounded-2xl overflow-hidden border border-line bg-white shadow-sm" data-testid={`admin-day-${date}`}>
                <div className="bg-cream-100 border-b border-line px-5 py-3 flex items-center justify-between">
                  <h3 className="font-heading text-lg text-ink-900">
                    {formatLongDate(date)}
                  </h3>
                  <span className="text-xs uppercase tracking-widest text-ink-500">
                    {formatShortDate(date, settings.date_format)}
                  </span>
                </div>
                <ul className="divide-y divide-line">
                  {list.map((s) => (
                    <li key={s.id} className="px-5 py-4 flex items-center gap-4" data-testid={`admin-slot-${s.id}`}>
                      <div className="w-24 shrink-0 text-ink-500 font-medium tabular-nums flex items-center gap-1.5">
                        <Clock size={12} strokeWidth={1.75} className="opacity-60" />
                        <span className="text-sm">{formatTime(s.start_time, timeFormat)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {s.booked ? (
                          <div>
                            <div className="font-medium text-ink-900">{s.booked.name}</div>
                            <div className="flex flex-wrap gap-3 mt-0.5 text-xs text-ink-500">
                              {s.booked.email && <span className="flex items-center gap-1"><Mail size={12} strokeWidth={1.75} />{s.booked.email}</span>}
                              {s.booked.phone && <span className="flex items-center gap-1"><Phone size={12} strokeWidth={1.75} />{s.booked.phone}</span>}
                            </div>
                          </div>
                        ) : (
                          <span className="text-ink-300 italic">Disponible</span>
                        )}
                      </div>
                      {s.booked && (
                        <button
                          onClick={() => removeBooking(s.id)}
                          className="text-ink-300 hover:text-coral"
                          aria-label="Cancelar reserva"
                          data-testid={`admin-cancel-${s.id}`}
                        >
                          <Trash2 size={16} strokeWidth={1.75} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {slotsByDate.size === 0 && <p className="text-ink-500">No hay horarios generados.</p>}
          </div>
        ) : (isPotluck) ? (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="font-heading text-2xl text-ink-900 mb-4">Espacios</h2>
              <div className="space-y-3">
                {event.slots.map((s) => (
                  <div key={s.id} className="bg-white border border-line rounded-2xl p-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-ink-900">{s.title}</div>
                      {s.when && <div className="text-xs text-ink-500">{s.when}</div>}
                    </div>
                    <span className="text-sm text-sage font-medium">{s.filled}/{s.quantity}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl text-ink-900 mb-4">Quién se apuntó</h2>
              {signups.length === 0 ? (
                <p className="text-ink-500 text-sm">Todavía nadie. Comparte el link para empezar.</p>
              ) : (
                <div className="space-y-3">
                  {signups.map((su, i) => (
                    <div key={su.id} className="bg-white border border-line rounded-2xl p-4" data-testid={`signup-row-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium text-ink-900">
                            {su.name} {su.quantity > 1 && <span className="text-ink-500">×{su.quantity}</span>}
                          </div>
                          <div className="text-xs text-ink-500 mt-0.5">{su.slot_title}</div>
                          {su.email && <div className="text-xs text-ink-500">{su.email}</div>}
                          {su.comment && <div className="text-sm text-ink-700 mt-1.5 italic">&ldquo;{su.comment}&rdquo;</div>}
                        </div>
                        <button onClick={() => removeSignup(su.id)} className="text-ink-300 hover:text-coral" aria-label="Eliminar" data-testid={`remove-signup-${i}`}>
                          <Trash2 size={16} strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <SubmissionsAdminView
            event={event}
            submissions={submissions}
            onRefresh={load}
          />
        )}
      </div>
    </div>
  );
}

function SubmissionsAdminView({ event, submissions, onRefresh }) {
  const toggle = async (sid, field) => {
    try {
      const cur = submissions.find((s) => s.id === sid);
      await api.patch(`/events/${event.id}/submissions/${sid}`, { [field]: !cur[field] });
      toast.success(field === "paid" ? "Estado de pago actualizado" : "Check-in actualizado");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (sid) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/events/${event.id}/submissions/${sid}`);
      toast.success("Eliminado");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const goal = event.goal;
  const isTicket = event.event_type === "tickets";
  const isForm = event.event_type === "form";
  const isPayment = ["gift", "fundraiser", "dues", "store", "tickets"].includes(event.event_type);

  return (
    <div className="mt-10 space-y-6">
      {goal && (event.event_type === "gift" || event.event_type === "fundraiser") && (
        <div className="bg-white border border-line rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-ink-500">Meta</div>
          <div className="mt-1 font-heading text-3xl text-ink-900">
            {formatMoney(event.total_collected || 0, goal.currency)}
            <span className="text-base text-ink-500 ml-2 font-body">de {formatMoney(goal.target_amount, goal.currency)}</span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-cream-100 overflow-hidden">
            <div className="h-full bg-coral" style={{ width: `${goal.target_amount > 0 ? Math.min(100, ((event.total_collected || 0) / goal.target_amount) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      <h2 className="font-heading text-2xl text-ink-900">Registros</h2>
      {submissions.length === 0 ? (
        <p className="text-ink-500 text-sm">Sin registros todavía. Comparte el link para empezar.</p>
      ) : (
        <div className="space-y-3">
          {submissions.map((s, i) => (
            <div key={s.id} className="bg-white border border-line rounded-2xl p-4" data-testid={`submission-row-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink-900">{s.anonymous ? "Anónimo" : s.name}</div>
                  <div className="flex flex-wrap gap-3 mt-0.5 text-xs text-ink-500">
                    {s.email && <span className="flex items-center gap-1"><Mail size={12} strokeWidth={1.75} />{s.email}</span>}
                    {s.phone && <span className="flex items-center gap-1"><Phone size={12} strokeWidth={1.75} />{s.phone}</span>}
                  </div>
                  {s.amount != null && (
                    <div className="mt-1 text-sm text-ink-900 font-medium">{formatMoney(s.amount, event.goal?.currency || event.dues_config?.currency || event.ticket_types?.[0]?.currency || event.store_products?.[0]?.currency || "MXN")}</div>
                  )}
                  {isTicket && s.ticket_type_name && (
                    <div className="mt-1 text-xs text-ink-500">Boleto: {s.ticket_type_name} · {s.quantity} pza(s)</div>
                  )}
                  {s.order_items && s.order_items.length > 0 && (
                    <ul className="mt-1 text-xs text-ink-500">
                      {s.order_items.map((it, idx) => <li key={idx}>{it.product_name} × {it.quantity} — {formatMoney(it.line_total, "MXN")}</li>)}
                    </ul>
                  )}
                  {isForm && s.form_values && (
                    <div className="mt-2 text-xs text-ink-700 space-y-0.5">
                      {(event.form_fields || []).map((f) => (
                        <div key={f.id}><b className="text-ink-500">{f.label}:</b> {String(s.form_values[f.id] || "—")}</div>
                      ))}
                    </div>
                  )}
                  {s.note && <div className="mt-1 text-sm text-ink-700 italic">&ldquo;{s.note}&rdquo;</div>}
                  {s.signature_data && <div className="mt-1 text-xs text-ink-500">Firma: <span className="italic font-heading text-ink-700">{s.signature_data}</span></div>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {isPayment && (
                    <button
                      onClick={() => toggle(s.id, "paid")}
                      className={`text-xs uppercase tracking-widest rounded-full px-3 py-1 border ${s.paid ? "bg-sage/10 border-sage/30 text-sage-hover" : "bg-cream-100 border-line text-ink-500 hover:text-coral"}`}
                      data-testid={`toggle-paid-${i}`}
                    >
                      {s.paid ? (<><CheckCircle2 size={12} strokeWidth={1.75} className="inline mr-1" /> Pagado</>) : "Marcar pagado"}
                    </button>
                  )}
                  {isTicket && (
                    <button
                      onClick={() => toggle(s.id, "checked_in")}
                      className={`text-xs uppercase tracking-widest rounded-full px-3 py-1 border ${s.checked_in ? "bg-sage/10 border-sage/30 text-sage-hover" : "bg-cream-100 border-line text-ink-500 hover:text-coral"}`}
                      data-testid={`toggle-checkin-${i}`}
                    >
                      {s.checked_in ? "Check-in ✓" : "Check-in"}
                    </button>
                  )}
                  <button
                    onClick={() => remove(s.id)}
                    className="text-ink-300 hover:text-coral"
                    aria-label="Eliminar"
                    data-testid={`remove-submission-${i}`}
                  >
                    <Trash2 size={14} strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
